// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  Box,
  Dialog as MDialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { FORTA_BLACK, FORTA_BLUE, FORTA_ORANGE, FORTA_WHITE } from '../../../utils/colors';
import { getThemeColor } from '../../../utils/common';
import FWarningPoper from '../FWarningPoper';
import { Themes } from '../../../constants';
import CloseIcon from '../../svgs/CloseIcon';

import type {
  DialogProps as MDialogProps,
  StandardTextFieldProps as MTextFieldProps,
} from '@mui/material';
import type { ChangeEvent } from 'react';

interface ExpandDialogProps extends Omit<MDialogProps, 'onClose'> {
  title?: string;
  content?: string;
  poperMessage?: string;
  minLength?: number;
  colorTheme?: Themes;
  disabled?: boolean;
  value?: any;
  onClose?: () => void;
  onEdit?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

interface TextFieldProps extends MTextFieldProps {
  showPopper?: boolean;
  colorTheme?: Themes;
}

const StyledDialog = styled(MDialog, {
  shouldForwardProp: (prop) => prop !== 'colorTheme' && prop !== 'poperMessage',
})<ExpandDialogProps>(({ colorTheme }: any) => ({
  '& .MuiDialog-paper': {
    width: 712,
    height: 500,
    maxWidth: 712,
    backgroundColor: colorTheme ? getThemeColor(colorTheme).light1 : FORTA_BLUE.light1,
    boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
    borderRadius: '10px',
  },
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },

  '& ::-webkit-scrollbar-thumb': {
    background: getThemeColor(colorTheme).primary,
  },
  '& .MuiModal-backdrop': {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
}));

const StyledTextarea = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'showPopper' && prop !== 'colorTheme' && prop !== 'error',
})<TextFieldProps>(({ showPopper, colorTheme, error }: any) => ({
  backgroundColor: FORTA_WHITE,
  borderRadius: '10px',
  boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).primary}`,
  maxHeight: showPopper ? '380px' : '427px',
  overflow: 'hidden',
  height: '100%',

  '&:after': error
    ? {
        content: '"!"',
        textAlign: 'center',
        width: 21,
        height: 21,
        backgroundColor: FORTA_ORANGE,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        color: FORTA_WHITE,
        fontWeight: 'bold',
        borderRadius: '50%',
        position: 'absolute',
        bottom: 5,
        left: 5,
      }
    : {},

  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    padding: '0.5em',
    fontSize: 16,
    height: '100%',
    alignItems: 'flex-start',

    '& .MuiInput-input.Mui-disabled': {
      WebkitTextFillColor: FORTA_BLACK,
    },

    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
  },

  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: 0,
    backgroundColor: FORTA_WHITE,
    color: getThemeColor(colorTheme).primary,
    fontWeight: 'bold',
    paddingLeft: '2em',
    fontSize: '1em',
    borderRadius: 10,
  },
}));

function ExpandDialog(props: ExpandDialogProps) {
  const {
    open,
    title,
    poperMessage = '',
    minLength = 0,
    colorTheme = Themes.Primary,
    disabled = false,
    value = '',
    onClose,
    onEdit,
    ...rest
  } = props;
  const [focusing, setFocusing] = useState<boolean>(false);
  const [showPopper, setShowPopper] = useState<boolean>(false);
  const [changeEvent, setChangeEvent] = useState<ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement
  > | null>(null);
  const [content, setContent] = useState<string>(value);

  useEffect(() => {
    if (!open) setChangeEvent(null);
  }, [open]);

  useEffect(() => {
    setShowPopper(focusing && poperMessage?.trim().length > 0);
  }, [focusing, poperMessage]);

  useEffect(() => {
    setContent(value);
  }, [value]);

  return (
    <StyledDialog
      {...rest}
      open={open}
      colorTheme={colorTheme}
      BackdropProps={{ style: { backgroundColor: 'transparent' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2,
        }}
      >
        <Typography fontWeight="bold" color={FORTA_WHITE} align="left">
          {title}
        </Typography>
        <IconButton sx={{ p: 0 }} disableRipple onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {showPopper && <FWarningPoper message={poperMessage} sx={{ top: 0, left: 24 }} />}
        <Box
          sx={{
            paddingTop: showPopper ? '48px' : 0,
            width: '100%',
            height: '100%',
          }}
        >
          <StyledTextarea
            sx={{ overflowY: 'auto' }}
            autoFocus
            variant="standard"
            multiline
            fullWidth={true}
            minRows={showPopper ? 15 : 17}
            colorTheme={colorTheme}
            showPopper={showPopper}
            disabled={disabled}
            value={content}
            onChange={(e) => {
              if (e.currentTarget instanceof HTMLTextAreaElement) {
                setChangeEvent(e);
              }
              setContent(e.target.value);
            }}
            onFocus={() => setFocusing(true)}
            onBlur={() => {
              setFocusing(false);
              if (onEdit && changeEvent) {
                onEdit(changeEvent);
              }
            }}
            error={focusing && content?.trim().length < minLength}
            helperText={
              focusing && content?.trim().length < minLength
                ? `Minimum ${minLength} characters`
                : ''
            }
          />
        </Box>
      </DialogContent>
    </StyledDialog>
  );
}

export default ExpandDialog;
